import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useNotification } from "@/context/NotificationContext";
import {
  useMissionControlSubscription,
  type V2AlertRaisedPayload,
} from "@/context/MissionControlWebSocketContext";
import { useCanAccessGuardians } from "@/hooks/useCanAccessGuardians";
import { useOsNavigate } from "@/components/ui/os-layout";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import { guardiansAlertsUrl } from "@/navigation/apps/guardiansLocation";
import {
  getGuardiansAlertNotificationsEnabled,
  setGuardiansAlertNotificationsEnabled,
  subscribeGuardiansAlertNotificationsPref,
} from "./guardiansAlertNotificationsPref";

const toastedAlertIds = new Set<string>();

/** Subscribe to screening alert WS events and show toasts when user prefs allow. */
export function useGuardianAlertNotifications(): void {
  const canAccess = useCanAccessGuardians();
  const { showWarning } = useNotification();
  const osNavigate = useOsNavigate();
  const location = useLocation();
  const notificationsEnabledRef = useRef(getGuardiansAlertNotificationsEnabled());

  useEffect(() => {
    return subscribeGuardiansAlertNotificationsPref(() => {
      notificationsEnabledRef.current = getGuardiansAlertNotificationsEnabled();
    });
  }, []);

  useEffect(() => {
    if (!canAccess) {
      setGuardiansAlertNotificationsEnabled(false);
      notificationsEnabledRef.current = false;
      return;
    }
    let cancelled = false;
    GuardiansV2API.getUserPreferences()
      .then((prefs) => {
        if (!cancelled) {
          const enabled = prefs.alert_notifications_enabled === true;
          setGuardiansAlertNotificationsEnabled(enabled);
          notificationsEnabledRef.current = enabled;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGuardiansAlertNotificationsEnabled(false);
          notificationsEnabledRef.current = false;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess]);

  const handleAlertRaised = useCallback(
    (payload: V2AlertRaisedPayload) => {
      if (!canAccess || !notificationsEnabledRef.current) return;
      if (payload.source !== "screening" || payload.notify !== true) return;
      if (toastedAlertIds.has(payload.alert_id)) return;

      const params = new URLSearchParams(location.search);
      const onGuardiansAlert =
        location.pathname.includes("/ops/guardians") && params.get("alert") === payload.alert_id;
      if (onGuardiansAlert) return;

      toastedAlertIds.add(payload.alert_id);
      const headline = payload.title?.trim() || payload.summary;
      showWarning(headline, undefined, {
        duration: 12,
        action: {
          label: "View",
          onClick: () => {
            osNavigate(guardiansAlertsUrl({ alertId: payload.alert_id }));
          },
        },
      });
    },
    [canAccess, location.pathname, location.search, osNavigate, showWarning],
  );

  useMissionControlSubscription(
    canAccess
      ? {
          onAlertRaised: handleAlertRaised,
        }
      : {},
  );
}
